<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { api } from '$lib/api/client';
  import type { Page, Card, CardType } from '$lib/api/types';

  export let page: Page;
  export let cards: Card[] = [];

  const dispatch = createEventDispatcher<{
    cardCreated: Card;
    pageUpdated: Page;
  }>();

  const GRID_COLS = 8;

  let creating = false;

  const cardTypes: { type: CardType; icon: string; label: string }[] = [
    { type: 'link', icon: 'lni-link-2-angular-right', label: 'リンク' },
    { type: 'text', icon: 'lni-text-format', label: 'テキスト' },
    { type: 'image', icon: 'lni-photos', label: '画像' },
    { type: 'video', icon: 'lni-play', label: '動画' },
    { type: 'section', icon: 'lni-minus', label: 'セクション' },
  ];

  const themePresets = [
    { value: 'light', label: 'ライト' },
    { value: 'dark', label: 'ダーク' },
    { value: 'kawaii', label: 'かわいい' },
    { value: 'cyber', label: 'サイバー' },
    { value: 'vrchat', label: 'VRChat' },
  ];

  // Find first available position for a new card (at the bottom)
  function findAvailablePosition(width: number, height: number): { x: number; y: number } {
    if (cards.length === 0) {
      return { x: 0, y: 0 };
    }

    // Find the lowest y position occupied by any card
    const maxY = Math.max(...cards.map(c => c.y + c.height));

    // Try to fit the card starting from the bottom
    for (let y = maxY; y >= 0; y--) {
      for (let x = 0; x <= GRID_COLS - width; x++) {
        if (canPlaceAt(x, y, width, height)) {
          return { x, y };
        }
      }
    }

    // If no space found, place at the very bottom
    return { x: 0, y: maxY };
  }

  // Check if a card can be placed at the given position
  function canPlaceAt(x: number, y: number, width: number, height: number): boolean {
    // Check bounds
    if (x < 0 || y < 0 || x + width > GRID_COLS) return false;

    // Check overlap with existing cards
    for (const card of cards) {
      const overlaps = !(
        x + width <= card.x ||
        card.x + card.width <= x ||
        y + height <= card.y ||
        card.y + card.height <= y
      );
      if (overlaps) return false;
    }
    return true;
  }

  async function createCard(type: CardType) {
    if (creating) return;
    creating = true;

    try {
      // Get default data and size for card type
      const data = getDefaultCardData(type);
      const width = type === 'section' ? 8 : 4;
      const height = type === 'section' ? 1 : 2;

      // Find position at the bottom that doesn't overlap
      const { x, y } = findAvailablePosition(width, height);

      const card = await api.cards.create({
        type,
        x,
        y,
        width,
        height,
        data,
      });

      dispatch('cardCreated', card);
    } catch (error) {
      console.error('Failed to create card:', error);
    } finally {
      creating = false;
    }
  }

  function getDefaultCardData(type: CardType): Record<string, unknown> {
    switch (type) {
      case 'link':
        return { url: 'https://example.com' };
      case 'text':
        return { content: '', style: 'body', fontSize: 'M' };
      case 'image':
        return { imageUrl: '', alt: '' };
      case 'video':
        return { videoUrl: '', loop: false, autoplay: false, mute: true };
      case 'section':
        return { label: 'セクション', fontSize: 'M' };
      default:
        return {};
    }
  }

  async function handleThemeChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const theme = select.value;

    try {
      const updated = await api.pages.update({ theme });
      dispatch('pageUpdated', updated);
    } catch (error) {
      console.error('Failed to update theme:', error);
    }
  }
</script>

<div class="toolbar">
  <div class="max-w-4xl mx-auto flex items-center justify-between gap-4">
    <!-- Theme Selector -->
    <div class="flex items-center gap-2">
      <label for="theme" class="text-sm text-gray-600">テーマ:</label>
      <select
        id="theme"
        value={page.theme}
        on:change={handleThemeChange}
        class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
      >
        {#each themePresets as preset}
          <option value={preset.value}>{preset.label}</option>
        {/each}
      </select>
    </div>

    <!-- Card Type Buttons -->
    <div class="flex items-center gap-2">
      {#each cardTypes as { type, icon, label }}
        <button
          on:click={() => createCard(type)}
          disabled={creating}
          class="btn btn-ghost flex items-center gap-1.5 text-sm"
          title={label}
        >
          <i class="lni {icon}"></i>
          <span class="hidden sm:inline">{label}</span>
        </button>
      {/each}
    </div>

    <!-- Share Button -->
    <button
      class="btn btn-secondary text-sm"
      on:click={() => navigator.clipboard.writeText(window.location.href)}
    >
      共有
    </button>
  </div>
</div>
